import {
  IconChevronRight,
  IconFolderTile,
  IconLayers,
  IconStandardBox,
  LockOutlined,
} from '../../../components/icons'
import { AvatarStack } from '../../../components/ui/Avatar'
import { formatDate } from '../format'
import type { WorkspaceSummary } from '../types'
import { tintClassForWorkspace } from '../workspace-tint'

export interface WorkspaceListRowProps {
  readonly workspace: WorkspaceSummary
  readonly onOpen: (id: string) => void
}

/**
 * UID-373 — one row of the Projects **List** view, comp
 * `Fictiv Platform.dc.html:1647-1668` ("VERTICAL LIST VIEW (dense, for many
 * projects)"). Before this, `/projects` List view rendered the very same
 * vertical `WorkspaceCard` as Grid view — only the container class differed —
 * so "List" was just the same column-flexed card stacked repeatedly, with
 * nothing lining up into row-columns. This is the dedicated horizontal row the
 * comp actually specifies; `WorkspaceCard` is untouched and still backs Grid
 * view.
 *
 * Field order, left to right, per the comp (`:1651-1664`): leading visual →
 * name (+ lock) → BOM count → part count → [member avatars] → updated date →
 * chevron.
 *
 * Three deliberate, precedent-following divergences from the comp's raw markup:
 *
 * 1. **A real `<button>`**, not the comp's `<div onClick>` — click, Enter,
 *    Space, focus ring and role come for free. Same posture `WorkspaceCard`
 *    already takes, and the same divergence this codebase makes everywhere the
 *    comp's HTML lacks semantics it needs (see the view-toggle pill,
 *    `AllWorkspacesPage.tsx:119`).
 *
 * 2. ~~The leading visual is an `Avatar`, not the comp's tinted folder-glyph
 *    box.~~ **RESOLVED by UID-392** — it is now that tinted box (`:1652`, 36px,
 *    `border-radius:4px`), tinted by `tintClassForWorkspace(workspace.id)`.
 *    UID-373 reasoned that the tint was "unrenderable" because
 *    `WorkspaceSummary` carries no tint/colour field; that premise was wrong —
 *    the sidebar had ALREADY solved it (UID-367) by DERIVING the tint from the
 *    immutable `workspace.id` rather than reading a server field, which needs
 *    no contract change. This row now shares that exact function with the
 *    sidebar and the grid card, so one project is one colour on all three
 *    surfaces. A genuinely persisted tint column remains the fully-faithful
 *    fix and remains a backend change.
 *
 * 3. ~~**No description line**, despite the comp's `:1655`. `WorkspaceSummary`
 *    (the `GET /api/workspaces` list projection) has NO `description` field —
 *    only the single-workspace `Workspace` type does — so there is nothing to
 *    render. Inventing a placeholder would assert data the BFF never sent.~~
 *    **RESOLVED by UID-393** — the contract gap is CLOSED rather than worked
 *    around. `description` is now a required, nullable field on the
 *    `WorkspaceSummary` schema (`openapi.yaml`), threaded through
 *    `WorkspaceSummaryView`/`#summaryRow` in `workspace-service.ts`; it is a
 *    plain owned column on the workspace row, so it needed no new derivation,
 *    no membership gate and no repository change. The line below therefore
 *    renders REAL BFF data — the original reasoning (don't fabricate) still
 *    holds and is exactly why this was fixed contract-first instead of with a
 *    placeholder. `WorkspaceCard` gained its own comp block's description line
 *    (`:1574`) in the same change.
 *
 * UID-375 — the member avatar stack (`:1659-1661`) is wired up below, between
 * the part-count span and the updated-date span per the comp's field order.
 * It renders from `workspace.membersPreview` (the UID-374 capped-at-3
 * preview) with `AvatarStack`'s `totalCount={workspace.memberCount}` driving
 * the true `+N` overflow badge — `membersPreview.length` alone can never
 * exceed 3, so it could never reveal overflow on its own. Branches on
 * presence (never `?? []`, per ADR-0114 D2/D6/D7: absence is not "no
 * members"); when `membersPreview` is absent, the slot stays ABSENT rather
 * than stubbed, same posture as the rest of this row.
 */
export function WorkspaceListRow({ workspace, onOpen }: WorkspaceListRowProps) {
  // Identical computation to `WorkspaceCard.tsx:22`. Per the frozen list
  // contract a private workspace the caller isn't a member of is never
  // returned at all, so this shouldn't occur today — the affordance is kept
  // defensively, and to match the comp's `p.locked` state (`:1654`).
  const isLockedPrivate = workspace.visibility === 'private' && workspace.myRole === null
  // UID-393 — identical normalization to `WorkspaceCard.tsx`: `null` (no
  // description set) and a whitespace-only string both mean "render no line".
  const description = workspace.description?.trim() ?? ''

  return (
    <button
      type="button"
      // The private state is folded into the button's OWN accessible name
      // rather than carried on the lock glyph. An `aria-label` on a button
      // overrides ALL descendant content for name computation, so an
      // `aria-label` on the nested icon would never be announced — it would be
      // visible-only, silently dropping the state for screen-reader users.
      // (`WorkspaceCard.tsx:33-35` has that latent gap; not fixed here because
      // this ticket's AC3 freezes the grid card, but it should not be copied.)
      aria-label={
        isLockedPrivate
          ? `Open ${workspace.name} (private — you are not a member)`
          : `Open ${workspace.name}`
      }
      className="workspace-list-row"
      onClick={() => onOpen(workspace.id)}
    >
      {/* UID-392 — comp `:1652`: the 36px square `border-radius:4px` tinted
       * swatch with a 19px folder glyph (the grid card's is the same swatch at
       * 44px/23px). Shares `tintClassForWorkspace` with the sidebar and the
       * grid card so a project reads as one colour everywhere; `aria-hidden`
       * because it is decoration beside the row's own visible name, and the
       * button's accessible name already carries the row's state. See
       * divergence 2 in the component comment above, and `../workspace-tint`. */}
      <span
        className={`workspace-list-row-icon ${tintClassForWorkspace(workspace.id)}`}
        aria-hidden="true"
      >
        <IconFolderTile size={19} />
      </span>

      <span className="workspace-list-row-main">
        <span className="workspace-list-row-name-line">
          <span className="workspace-list-row-name">{workspace.name}</span>
          {/* Decorative — the state it conveys is in the button's accessible
           * name above, so announcing it here too would be a duplicate. `title`
           * is kept for the mouse-hover tooltip. `className` rather than
           * styling ant-design's internal `.anticon` from CSS: that class is a
           * third-party implementation detail and no other stylesheet in
           * `apps/web/src/styles/` targets it. */}
          {isLockedPrivate && (
            <LockOutlined
              aria-hidden="true"
              title="Private project"
              className="workspace-list-row-lock"
            />
          )}
        </span>
        {/* UID-393 — comp `:1655`: 12.5px, clipped to a SINGLE line with an
         * ellipsis (`white-space:nowrap;overflow:hidden;text-overflow:ellipsis`).
         * That single-line clip is the deliberate difference from the grid
         * card's description, which the comp lets wrap freely — this view is the
         * dense one, so a long description must not push the row taller.
         *
         * Sits inside `.workspace-list-row-main` (the comp's `flex:1;min-width:0`
         * column), which is what gives the ellipsis something finite to clip
         * against. Same null/blank presence guard as the grid card. */}
        {description !== '' && <span className="workspace-list-row-desc">{description}</span>}
      </span>

      {/* Counts are gated on their OWN presence with `!== undefined`, never a
       * falsy check and never `?? 0`: per ADR-0114 D2/D6/D7 the BFF omits these
       * fields for a non-member, on the embedded project-chip projections, and
       * when the derivation failed, so a `0` would assert "this project is
       * empty" on evidence that says no such thing. An explicit `0` (an empty
       * project the caller belongs to) DOES render. Plural nouns are
       * hard-coded, so one BOM reads "1 BOMs" — that is what the comp renders
       * (`:1657`) and matches the grid card's shipped behaviour (UID-348,
       * human product decision 2026-08-27). Same BOMs-before-parts order and
       * no separator between the two, per `WorkspaceCard.tsx:55-70`. */}
      {workspace.bomCount !== undefined && (
        <span className="workspace-list-row-count">
          <IconStandardBox size={14} />
          {workspace.bomCount} BOMs
        </span>
      )}
      {workspace.partCount !== undefined && (
        <span className="workspace-list-row-count">
          <IconLayers size={14} />
          {workspace.partCount} parts
        </span>
      )}

      {/* UID-375 — comp `:1659-1661`. Same presence branch and `totalCount`
       * wiring as `WorkspaceCard.tsx`'s grid-card footer stack, at the comp's
       * list-row size (26px) rather than the grid card's 28px. */}
      {workspace.membersPreview !== undefined && workspace.membersPreview.length > 0 && (
        <AvatarStack
          people={workspace.membersPreview.map((m) => ({
            id: m.userId,
            name: m.displayName,
            avatarUrl: m.avatarUrl,
          }))}
          max={3}
          size={26}
          totalCount={workspace.memberCount}
        />
      )}

      {/* Comp `:1663`. `formatDate` is the shared ISO → "Mon D, YYYY" helper
       * already used for this exact `updatedAt` job by WorkspacePage,
       * WorkspacePartsTab and WorkspaceRfqsTab. */}
      <span className="workspace-list-row-updated">{formatDate(workspace.updatedAt)}</span>

      <IconChevronRight />
    </button>
  )
}
